import React, { FunctionComponent, useState, useRef, useEffect } from "react";
import debounce from "lodash.debounce";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";

import { FIRST_NAME_PATH, SUB_CATEGORY_PATH } from "../../routing/paths";
import { Spinner } from "../Spinner";
import { Invocations, SelectedBusiness, Publisher } from "../../containers/Wizard";
import { Business } from "../../containers/BusinessStep/actions";

const SELECT_BUSINESS_SPELL = "business";
const NEW_BUSINESS_NAME_SPELL = "business_name";

export type BusinessStepProps = {
  loading: boolean;
  businesses: Array<Business>;
  invocate: Function;
  deleteInvocations: Function;
  getBusinesses: Function;
  invocations: Invocations;
  changePath: Function;
  publisher: Publisher;
};

const BusinessStep: FunctionComponent<BusinessStepProps> = ({
  loading,
  businesses,
  invocate,
  deleteInvocations,
  getBusinesses,
  invocations,
  changePath,
  publisher,
}: BusinessStepProps) => {
  const selectedBiz = invocations[SELECT_BUSINESS_SPELL] as SelectedBusiness;
  const newBizName = invocations[NEW_BUSINESS_NAME_SPELL] as string;

  const [bizName, setBizName] = useState("");
  const [spinner, setSpinner] = useState(false);

  useEffect(() => {
    const selectedBizName = selectedBiz?.name;
    if (selectedBizName) {
      setBizName(selectedBizName);
      getBusinesses(selectedBizName);
    }
  }, [invocations, setBizName, getBusinesses, selectedBiz]);

  useEffect(() => {
    if (newBizName) setBizName(newBizName);
  }, [invocations, newBizName, setBizName]);

  useEffect(() => {
    setSpinner(loading);
  }, [loading, setSpinner]);

  const delayedSearch = useRef(debounce((query: string) => getBusinesses(query), 300))
    .current;

  const handleBizNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const bizName = e.target.value;
    setBizName(bizName);
    setSpinner(bizName !== "");
    if (bizName !== "") delayedSearch(bizName);
  };

  const handleBizSelected = (biz: Business) => {
    if (invocations[NEW_BUSINESS_NAME_SPELL]) {
      deleteInvocations([NEW_BUSINESS_NAME_SPELL]);
    }
    invocate(SELECT_BUSINESS_SPELL, biz.uuid, FIRST_NAME_PATH);
    setBizName(biz.name);
  };

  const handleBizInput = () => {
    if (invocations[SELECT_BUSINESS_SPELL]) {
      deleteInvocations(
        [
          SELECT_BUSINESS_SPELL,
          "address",
          "business_phone",
          "city",
          "email",
          "facebook",
          "instagram",
          "state",
          "twitter",
          "website",
          "zip",
        ]
      );
    }
    invocate(NEW_BUSINESS_NAME_SPELL, bizName, SUB_CATEGORY_PATH);
  }

  const renderBusinesses = () => (
    <div>
      <h3>
        {businesses.length > 0
          ? "Is one of these your business?"
          : "No business could be found"}
      </h3>
      <ul className="options">
        {businesses.map((biz: Business) => (
          <li key={biz.name} tabIndex={0} onClick={() => handleBizSelected(biz)}>
            <div className="strong">{biz.name}</div>
            <div className="weak">{biz.formatted_address}</div>
          </li>
        ))}

        <li tabIndex={0} onClick={handleBizInput}>
          <div className="strong">
            {businesses.length > 0 ? "None of the above" : "Add your business"}
          </div>
        </li>
      </ul>
    </div>
  );

  const og_title = `Ad Builder - ${publisher.newspaper_name}`

  return (
    <div>
      <Helmet>
        <title>{og_title}</title>
        <meta name="og:title" content={og_title} />
      </Helmet>
      <div className={`question ${bizName !== "" ? "splitter" : ""}`}>
        <h3>
          What&apos;s the <strong>name</strong> of your business?
        </h3>
        <h4>We&apos;ll do our best to find all your information.</h4>
        <input
          className="business-name"
          type="text"
          placeholder="Type your answer here..."
          value={bizName}
          onChange={handleBizNameChanged}
          autoFocus
        />
      </div>
      {bizName.length > 0 && (
        <div className="results">
          {spinner && <Spinner />}
          {!spinner && renderBusinesses()}
        </div>
      )}
    </div>
  );
};

BusinessStep.propTypes = {
  loading: PropTypes.bool.isRequired,
  businesses: PropTypes.array.isRequired,
  invocate: PropTypes.func.isRequired,
  getBusinesses: PropTypes.func.isRequired,
  invocations: PropTypes.any,
  changePath: PropTypes.func.isRequired,
  publisher: PropTypes.any,
};

export default BusinessStep;
